import React, {useCallback, useState} from 'react';
import {
  AppBar,
  Backdrop,
  Box,
  Button,
  CircularProgress,
  Grid,
  List,
  ListItem,
  ListItemSecondaryAction,
  ListItemText,
  Paper,
  TextField,
  Toolbar
} from "@mui/material";
import {Edit, FileCopyOutlined, LinkOutlined, OpenInBrowser} from "@mui/icons-material";
import {ImpressButton} from "./utils/ImpressButton";
import {MessageSeverity, MessageSnackbar} from "./utils/MessageSnackbar";
import {addToastMessage} from "./stores/MessageStore";
import {shortenUrl} from "./clients/ApiClient";

interface ShortedUrl {
  url: string
  shortUrl: string
  editUrl: string
}

export const UrlyPage = () => {
  const [url, setUrl] = useState<string>('')
  const [errorLabel, setErrorLabel] = useState<string | undefined>()
  const [loading, setLoading] = useState<boolean>(false)
  const [shortUrl, setShortUrl] = useState<string | undefined>()
  const [shortedUrls, setShortedUrls] = useState<ShortedUrl[]>([])
  const shorten = useCallback(async () => {
    if (!url) {
      setErrorLabel('Please enter an url to shorten')
      return
    } else {
      setErrorLabel(undefined)
    }
    const alreadyShorted = shortedUrls.find(u => u.url === url)
    if (alreadyShorted) {
      setShortUrl(alreadyShorted.shortUrl)
      addToastMessage('Shortened again', MessageSeverity.success)
      return
    }
    try {
      setLoading(true)
      const response = await shortenUrl(url)

      setShortUrl(response.url)
      addToastMessage('Successfully shortened', MessageSeverity.success)
      setShortedUrls([{url: url!, shortUrl: response.url, editUrl: response.editUrl}, ...shortedUrls])
    } catch (e) {
      console.error(e)
      addToastMessage('something failed', MessageSeverity.error)
    } finally {
      setLoading(false)
    }
  }, [setLoading, setShortedUrls, url, shortedUrls])

  const onCopyToClipBoard = async () => {
    if (shortUrl) {
      await navigator.clipboard.writeText(shortUrl);
      addToastMessage('Copied to clipboard', MessageSeverity.success)
    }
  }
  const onCopyOldToClipBoard = async (toCopy: string, text: string = 'Copied to clipboard') => {
    if (toCopy) {
      await navigator.clipboard.writeText(toCopy);
      addToastMessage(text, MessageSeverity.success)
    }
  }

  const trimIfNecessary = (text: string) => {
    return text.length >= 30 ? text.substring(0, 29) + '...' : text
  }

  return (
    <div className="App" style={{marginBottom: 25, marginTop: 25}}>
      <Backdrop open={loading} style={{zIndex: 999}}><CircularProgress color="inherit"/></Backdrop>
      <Grid container>
        <Grid item sm={1} md={3} lg={4}/>
        <Grid item xs={12} sm={10} md={6} lg={4}>
          <Paper elevation={1} sx={t => ({
            backgroundColor: t.palette.secondary.main,
            padding: 3,
            maxWidth: 250,
            margin: 'auto',
          })}>
            <div style={{display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
              <img src="/android-chrome-512x512.png" alt="logo" style={{maxWidth: 37}}/>
              <h1>&nbsp;urly</h1>
            </div>
            <h4 style={{marginTop: -10}}>SHORTENING URLS</h4>
          </Paper>
          <Paper elevation={3} style={{marginTop: 25, marginLeft: 5, marginRight: 5, padding: 25}}>
            <Grid container>
              <Grid item sm={1}/>
              <Grid item xs={12} sm={10}>
                <TextField
                  defaultValue={url}
                  onChange={(e) => setUrl(e.target.value)}
                  inputMode="url"
                  label="Url to shorten"
                  error={!!errorLabel}
                  helperText={errorLabel}
                  fullWidth
                />
              </Grid>
            </Grid>
            <Box sx={{marginTop: 3}}>
              <Button
                startIcon={<LinkOutlined/>}
                variant="contained"
                onClick={shorten}
                color="primary"
              >Shorten</Button>
            </Box>
            {shortUrl && (
              <Paper
                elevation={2}
                sx={{marginTop: 3, padding: 2, maxWidth: 350, backgroundColor: '#f0f0f0', marginX: 'auto'}}
              >
                <span style={{fontSize: 18}}>{shortUrl}&nbsp;&nbsp;</span>
                <FileCopyOutlined
                  sx={{cursor: 'pointer'}}
                  fontSize="small"
                  onClick={onCopyToClipBoard}/>
              </Paper>
            )}
          </Paper>
        </Grid>
      </Grid>
      {!!shortedUrls.length && (
        <Grid container sx={{mb: 10}}>
          <Grid item sm={2} md={3} lg={4}/>
          <Grid item xs={12} sm={8} md={6} lg={4}>
            <Paper elevation={1}
                   sx={{marginTop: 3, marginX: 2, paddingTop: 1, paddingBottom: 0.5}}>
              <h2>Shortened urls</h2>
              <List sx={t => ({backgroundColor: t.palette.background.paper})}>
                {shortedUrls.map(({url, shortUrl, editUrl}, index) => (
                  <ListItem key={`${url}-${shortUrl}`} divider={index < shortedUrls.length - 1}>
                    <ListItemText primary={shortUrl} secondary={`-> ${trimIfNecessary(url)}`} style={{marginLeft: 10}}/>
                    <ListItemSecondaryAction>
                      <OpenInBrowser
                        style={{paddingTop: 5, paddingRight: 10, cursor: 'pointer'}}
                        fontSize="small"
                        onClick={() => window.open(editUrl, '_blank')}
                        />
                      <Edit
                        style={{paddingTop: 5, paddingRight: 10, cursor: 'pointer'}}
                        fontSize="small"
                        onClick={() => onCopyOldToClipBoard(editUrl, 'Copied edit url to clipboard')}
                        />
                      <FileCopyOutlined
                        style={{paddingTop: 5, cursor: 'pointer'}}
                        fontSize="small"
                        onClick={() => onCopyOldToClipBoard(shortUrl)}
                      />
                    </ListItemSecondaryAction>
                  </ListItem>
                ))}
              </List>
            </Paper>
          </Grid>
        </Grid>
      )}
      <AppBar position="fixed" color="secondary" sx={{
        top: 'auto',
        bottom: 0,
      }}>
        <Toolbar>
          <div style={{flexGrow: 1}}/>
          <ImpressButton/>
        </Toolbar>
      </AppBar>
      <MessageSnackbar/>
    </div>
  );
}
